import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import {
  generateTeamInvitation,
  createInvitationUrl,
} from "./services/teamInviteService";
import { useUser } from "@/contexts/UserContext";

interface TeamInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
}

export function TeamInviteModal({
  isOpen,
  onClose,
  teamId,
  teamName,
}: TeamInviteModalProps) {
  const { user } = useUser();
  const [inviteLink, setInviteLink] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [expiresIn, setExpiresIn] = useState(7); // Default 7 days

  // Generate an invitation link using the service
  const handleGenerateInviteLink = async () => {
    try {
      setIsGenerating(true);
      
      // Validate user
      if (!user?.id) {
        toast.error("You must be logged in to generate invitations");
        console.error("No user found");
        return;
      }

      const inviteCode = await generateTeamInvitation(
        user.id,
        teamId,
        expiresIn
      );

      if (inviteCode) {
        const inviteUrl = createInvitationUrl(inviteCode);
        setInviteLink(inviteUrl);
        toast.success("Invitation link generated successfully");
      } else {
        toast.error("Failed to generate invitation - please try again");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to generate invitation link");
      console.error("Error generating invite:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy the invitation link to clipboard
  const copyInviteLink = () => {
    if (!inviteLink) return;

    navigator.clipboard
      .writeText(inviteLink)
      .then(() => {
        setIsLinkCopied(true);
        toast.success("Invite link copied to clipboard");

        // Reset the copied state after 3 seconds
        setTimeout(() => setIsLinkCopied(false), 3000);
      })
      .catch((error) => {
        toast.error("Failed to copy link");
        console.error("Error copying link:", error);
      });
  };

  // Handle modal close and reset state
  const handleClose = () => {
    setInviteLink("");
    setIsLinkCopied(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite to {teamName}</DialogTitle>
          <DialogDescription>
            Generate a unique invitation link to share with others
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex items-center gap-4">
            <Label htmlFor="expires" className="min-w-[120px]">
              Link expires in
            </Label>
            <select
              id="expires"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={expiresIn}
              onChange={(e) => setExpiresIn(Number(e.target.value))}
              disabled={!!inviteLink}
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>

          {inviteLink ? (
            <div className="space-y-2">
              <Label htmlFor="link">Invitation Link</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="link"
                  value={inviteLink}
                  readOnly
                  className="flex-1"
                />
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={copyInviteLink}
                  className="flex-shrink-0"
                >
                  {isLinkCopied ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex sm:justify-between">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {!inviteLink ? (
            <Button onClick={handleGenerateInviteLink} disabled={isGenerating}>
              {isGenerating ? "Generating..." : "Generate Invite Link"}
            </Button>
          ) : (
            <Button onClick={handleClose} variant="default">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
